/**
 * Manual Referral Entry — portal-side form (Ticket, 2026-08-24, commit 2 of 2).
 *
 * Directly hits POST /api/intake/manual-referral. See the ticket for the
 * INSERT + Step 3 handshake — this file is UI only.
 *
 * House rules pinned here:
 *   • Every date input is a <input type="date"> whose native value is
 *     already YYYY-MM-DD — passed to the API as-is via dateOnly.js.
 *     NEVER `new Date(dob)` or `.toISOString()` (08-20 corruption class).
 *   • Case Type / Therapy / Direct Access changes reload the clinic
 *     dropdown from GET /clinics.
 *   • Payor autocomplete is debounced 250ms against GET /payors.
 *   • Uploads are multipart with a per-file label dropdown and a
 *     count guard on `Email Chain` (max 1, PDF-only).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toDateOnlyString } from "../lib/dateOnly";
import { useMyLocationCodes } from "../lib/useMyLocationCodes";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Same token-lookup as ReferralIntake — sessionStorage first, then
// localStorage. Kept inline (no shared lib) to match the rest of the app.
const getStoredToken = () =>
  sessionStorage.getItem("token") || localStorage.getItem("token");

// ─── vocab (mirrored from the backend) ─────────────────────────────
const THERAPIES = [
  { display: "Physical Therapy", code: "PT" },
  { display: "Acupuncture", code: "AP" },
  { display: "Aquatic Therapy", code: "AT" },
];
const DOC_LABELS = [
  "Prescription", "Authorization", "Authorization Request (RFA)",
  "Demographics", "Medical Records", "Plan of Care",
  "Progress Report (PR-2)", "Proof of Service", "Denial Letter",
  "Photo ID", "Email Chain", "Other",
];
const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN",
  "IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV",
  "NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN",
  "TX","UT","VT","VA","WA","WV","WI","WY","DC","PR",
];

// today() in local time — never `new Date().toISOString().slice(0,10)`
// because that returns UTC and clips wrong-day near midnight (08-20
// corruption class the house rule bars).
const localTodayYYYYMMDD = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const EMPTY_FIELDS = {
  employee_first_name: "", employee_last_name: "",
  date_of_birth: "", patient_birth_sex: "",
  case_type: "", therapy_type: "",
  patient_home_phone: "", patient_address: "", patient_city: "",
  patient_state: "", patient_zip_code: "", patient_ssn: "",
  body_parts_involved: "", referral_reason: "",
  date_of_accident: "", date_of_injury: "",
  location_code: "", assigned_clinic: "",
  assigned_clinic_address: "", assigned_clinic_city: "",
  assigned_clinic_zipcode: "",
  primary_physician_name: "", primary_physician_npi: "",
  primary_physician_phone: "", primary_physician_address: "",
  attorney_name: "", attorney_phone: "",
  attorney_address: "", attorney_city: "", attorney_state: "",
  attorney_zip_code: "", attorney_contact_person: "",
  attorney_contact_email: "",
  employer_name: "", claim_number: "",
  insurance_carrier: "", insurance_address: "", insurance_po_box: "",
  adjuster_name: "", adjuster_phone: "", adjuster_email: "",
  referral_coordinator_name: "", referral_coordinator_email: "",
  type_of_accident: "",
  icd10_codes: "", team_notes: "",
  direct_access: false, post_op: false,
  // Ticket 2026-08-24 §3 — hidden payor-code fields. Populated ONLY
  // from a roster pick; cleared when the user free-types over the
  // name. NEVER user-typed / user-editable. Submitted verbatim so
  // Step 3 can code-match deterministically.
  attorney_payor_code: "",
  insurance_payor_code: "",
};

// ─── small components ──────────────────────────────────────────────
function Field({ label, required, children, hint, error }) {
  return (
    <label className="block text-sm text-slate-700 mb-3">
      <span className="block font-medium mb-1">
        {label} {required && <span className="text-red-600">*</span>}
      </span>
      {children}
      {hint && <span className="block text-xs text-slate-500 mt-1">{hint}</span>}
      {error && <span className="block text-xs text-red-600 mt-1">{error}</span>}
    </label>
  );
}

function Section({ title, children }) {
  return (
    <fieldset className="border border-slate-200 rounded-lg p-4 mb-4">
      <legend className="text-sm font-semibold text-slate-800 px-2">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

// ── Payor autocomplete (untyped after 2026-08-24 §2) ─────────────────
// `caseType` drives the financial_class affinity sort on the server —
// SORT, NEVER FILTER. Every row is reachable from either case type;
// staff can pick an AUTO-class payor from a WC case and vice versa.
// `selectedCode` shows a small code chip inline once the pick came from
// the roster; it clears the moment the user free-types over the name.
function PayorAutocomplete({ caseType, value, selectedCode, onChange, onPick, required, disabled }) {
  const [q, setQ] = useState(value || "");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const dbRef = useRef();
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    if (!q || q.length < 2 || disabled) { setOptions([]); return; }
    clearTimeout(dbRef.current);
    dbRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API}/api/intake/manual-referral/payors`,
          {
            headers: { Authorization: `Bearer ${getStoredToken()}` },
            params: { case_type: caseType, q, limit: 10 },
          }
        );
        setOptions(res.data?.payors || []);
      } catch { setOptions([]); }
    }, 250);
    return () => clearTimeout(dbRef.current);
  }, [q, caseType, disabled]);
  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={q}
          required={required}
          disabled={disabled}
          onChange={(e) => { setQ(e.target.value); onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="w-full border border-slate-300 rounded px-2 py-1 disabled:bg-slate-100"
          data-testid={`${(caseType || 'payor').toLowerCase()}-autocomplete-input`}
        />
        {selectedCode && (
          <span
            className="text-xs font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-2 py-0.5 whitespace-nowrap"
            title="Payor code from Raintree — submitted as a hidden field"
            data-testid={`${(caseType || 'payor').toLowerCase()}-code-chip`}
          >
            {selectedCode}
          </span>
        )}
      </div>
      {open && options.length > 0 && (
        <ul className="absolute z-30 left-0 right-0 mt-1 border border-slate-300 bg-white rounded shadow max-h-72 overflow-auto"
            data-testid={`${(caseType || 'payor').toLowerCase()}-autocomplete-menu`}>
          {options.map((o) => {
            // §2 duplicate-name disambiguation: EVERY row shows
            // code + address + city so identical names (Sweet James
            // ×3, Sedgwick CMS ×66) are still distinguishable.
            const subtitle = [o.code, o.address, o.city, o.state]
              .filter(Boolean)
              .join(" · ");
            const fc = (o.financial_class || "").toUpperCase();
            return (
              <li
                key={o.code}
                className="px-2 py-1.5 hover:bg-slate-100 cursor-pointer text-sm"
                onMouseDown={() => { setQ(o.name); onPick(o); setOpen(false); }}
                data-testid={`payor-suggestion-${o.code}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">{o.name}</span>
                  {fc && (
                    <span
                      className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${fc === "AUTO"
                        ? "bg-amber-50 text-amber-700 border border-amber-200"
                        : fc === "WC"
                          ? "bg-sky-50 text-sky-700 border border-sky-200"
                          : "bg-slate-50 text-slate-600 border border-slate-200"}`}
                      data-testid={`payor-fc-chip-${o.code}`}
                    >
                      {fc}
                    </span>
                  )}
                </div>
                {subtitle && (
                  <div className="text-xs text-slate-500">{subtitle}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


// ── Physician autocomplete (Ticket 2026-08-24 §4) ────────────────────
// Matches on name / first_name + last_name / organization with
// reversed-name-order support ("John Smith" ↔ "Smith, John") — the
// server does the normalization; the client just renders. Office
// contacts (~650 C-prefix rows) show a subtle badge but remain
// selectable. On pick, name is rendered "First Last" in the field.
function PhysicianAutocomplete({ value, onChange, onPick, required, disabled }) {
  const [q, setQ] = useState(value || "");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const dbRef = useRef();
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    if (!q || q.length < 2 || disabled) { setOptions([]); return; }
    clearTimeout(dbRef.current);
    dbRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API}/api/intake/manual-referral/physicians`,
          {
            headers: { Authorization: `Bearer ${getStoredToken()}` },
            params: { q, limit: 10 },
          }
        );
        setOptions(res.data?.physicians || []);
      } catch { setOptions([]); }
    }, 250);
    return () => clearTimeout(dbRef.current);
  }, [q, disabled]);
  return (
    <div className="relative">
      <input
        type="text"
        value={q}
        required={required}
        disabled={disabled}
        onChange={(e) => { setQ(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="w-full border border-slate-300 rounded px-2 py-1 disabled:bg-slate-100"
        data-testid="physician-autocomplete-input"
      />
      {open && options.length > 0 && (
        <ul className="absolute z-30 left-0 right-0 mt-1 border border-slate-300 bg-white rounded shadow max-h-72 overflow-auto"
            data-testid="physician-autocomplete-menu">
          {options.map((o) => {
            const displayName = (o.first_name && o.last_name)
              ? `${o.first_name} ${o.last_name}`
              : (o.name || "");
            // §4 subtitle — the metadata line staff use to pick the
            // right one when physicians repeat (530 NPIs on multiple
            // rows; one MD has 17 entries).
            const meta = [o.credentials, o.organization, o.city, o.phone]
              .filter(Boolean)
              .join(" · ");
            return (
              <li
                key={o.code}
                className="px-2 py-1.5 hover:bg-slate-100 cursor-pointer text-sm"
                onMouseDown={() => {
                  const picked = { ...o, display_name: displayName };
                  setQ(displayName);
                  onPick(picked);
                  setOpen(false);
                }}
                data-testid={`physician-suggestion-${o.code}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">{displayName}</span>
                  {o.is_office_contact && (
                    <span
                      className="text-[10px] font-mono bg-slate-100 text-slate-600 border border-slate-200 rounded px-1.5 py-0.5"
                      title="Office / distribution contact (not an individual physician)"
                      data-testid={`physician-office-badge-${o.code}`}
                    >
                      office contact
                    </span>
                  )}
                </div>
                {meta && (
                  <div className="text-xs text-slate-500">{meta}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─── main page ─────────────────────────────────────────────────────
// Consent v0.3 (RATIFIED) — verbatim. Do not reflow or substitute
// characters: this text is filed as 10DLC opt-in evidence, and the version
// string travels with the captured consent.
export const SMS_CONSENT_CHECKBOX_TEXT =
  "I agree to receive appointment-related text messages from WestStar Physical Therapy. Msg & data rates may apply, frequency varies. Reply STOP to opt out. Consent is not a condition of treatment.";
export const SMS_CONSENT_FULL_TEXT =
  "By providing your mobile phone number, you agree to receive text messages from WestStar Physical Therapy about your care with us, including appointment confirmations, appointment reminders, requests to confirm your attendance, and secure links to complete required paperwork. Message frequency varies based on your appointments. Message and data rates may apply. Consent is not a condition of receiving treatment. You can opt out at any time by replying STOP to any message, or get help by replying HELP or calling 888-786-2888. We will not sell or share your mobile number with third parties for their marketing purposes. Privacy Policy: https://wsptn.com/legal/privacy-policy/ \u00b7 SMS Terms: https://wsptn.com/legal/sms-terms/";

export default function ManualReferralEntry() {
  const [fields, setFields] = useState(EMPTY_FIELDS);
  // Default UNCHECKED, never pre-checked (Consent v0.3 rule 4).
  const [smsConsent, setSmsConsent] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [clinicsLoading, setClinicsLoading] = useState(false);
  const [uploads, setUploads] = useState([]); // [{file, label}]
  const [submitting, setSubmitting] = useState(false);
  const [duplicates, setDuplicates] = useState(null); // {list, resolve} for the warning modal
  const [errorSummary, setErrorSummary] = useState("");

  const setField = useCallback((name, value) => {
    setFields((f) => ({ ...f, [name]: value }));
  }, []);

  const therapyCode = useMemo(
    () => THERAPIES.find((t) => t.display === fields.therapy_type)?.code || "",
    [fields.therapy_type]
  );
  const canLoadClinics = fields.case_type && therapyCode;

  useEffect(() => {
    if (!canLoadClinics) { setClinics([]); return; }
    let cancelled = false;
    setClinicsLoading(true);
    axios
      .get(`${API}/api/intake/manual-referral/clinics`, {
        headers: { Authorization: `Bearer ${getStoredToken()}` },
        params: { case_type: fields.case_type, therapy: therapyCode },
      })
      .then((r) => { if (!cancelled) setClinics(r.data?.clinics || []); })
      .catch(() => { if (!cancelled) setClinics([]); })
      .finally(() => { if (!cancelled) setClinicsLoading(false); });
    return () => { cancelled = true; };
  }, [fields.case_type, therapyCode, canLoadClinics]);

  // ── WS Location lock (Commit 2, 2026-09) ─────────────────────────────
  // A WS Location user files referrals FOR THEIR OWN CLINIC. The roster is
  // narrowed to their own sites and, with one site, the field stops being a
  // choice at all: it renders as locked text saying whose location it is.
  // This is a courtesy — the boundary is server-side, on the same endpoint
  // that serves both the full submit and the draft.
  const { codes: myLocationCodes, isLocationUser } = useMyLocationCodes();
  const myClinics = useMemo(() => (
    isLocationUser
      ? clinics.filter((c) => (myLocationCodes || []).includes(c.location_code))
      : clinics
  ), [clinics, isLocationUser, myLocationCodes]);
  const lockedToOneClinic = isLocationUser && myClinics.length === 1;

  useEffect(() => {
    if (!lockedToOneClinic) return;
    const c = myClinics[0];
    if (fields.location_code === c.location_code) return;
    setFields((f) => ({
      ...f,
      location_code: c.location_code,
      assigned_clinic: c.name || "",
      assigned_clinic_address: c.address || "",
      assigned_clinic_city: c.city || "",
      assigned_clinic_zipcode: c.zip || "",
    }));
  }, [lockedToOneClinic, myClinics, fields.location_code]);

  const isPI = fields.case_type === "PI";
  const isWC = fields.case_type === "WC";

  // ─── upload row helpers ────────────────────────────────────────
  const addFiles = (fileList) => {
    const added = Array.from(fileList).map((f) => ({
      file: f, label: "Prescription", // sensible default
    }));
    setUploads((u) => [...u, ...added]);
  };
  const removeUpload = (idx) => setUploads((u) => u.filter((_, i) => i !== idx));
  const setLabel = (idx, label) =>
    setUploads((u) => u.map((row, i) => (i === idx ? { ...row, label } : row)));

  const emailChainCount = uploads.filter((u) => u.label === "Email Chain").length;

  // ─── save handler ──────────────────────────────────────────────
  async function doSubmit(saveAsIncomplete) {
    // Duplicate label sanity (Email Chain max 1).
    if (emailChainCount > 1) {
      toast.error("Only one Email Chain document is allowed per referral.");
      return;
    }
    for (const u of uploads) {
      if (u.label === "Email Chain" && !u.file.name.toLowerCase().endsWith(".pdf")) {
        toast.error(`Email Chain must be a PDF — ${u.file.name}`);
        return;
      }
    }

    setErrorSummary("");
    setSubmitting(true);
    try {
      const form = new FormData();
      // Dates already YYYY-MM-DD (native date input); guard once more.
      const payload = { ...fields };
      for (const k of ["date_of_birth", "date_of_accident", "date_of_injury"]) {
        if (payload[k]) payload[k] = toDateOnlyString(payload[k]);
      }
      form.append("payload_json", JSON.stringify(payload));
      form.append("save_as_incomplete", saveAsIncomplete ? "true" : "false");
      // Consent v0.3: checked = staff captured the patient's explicit yes.
      // Unchecked sends false and the server writes NOTHING.
      form.append("sms_consent", smsConsent ? "true" : "false");
      form.append(
        "document_labels",
        JSON.stringify(uploads.map((u) => u.label))
      );
      for (const u of uploads) form.append("files", u.file);

      const res = await axios.post(
        `${API}/api/intake/manual-referral`,
        form,
        {
          headers: {
            Authorization: `Bearer ${getStoredToken()}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const body = res.data;

      // §9.1 duplicate warning — non-blocking. Only surface if the
      // caller pushed through and there's actually a potential match.
      if (!saveAsIncomplete && body.duplicates?.length > 0) {
        // The row is already saved; this is an informational modal.
        setDuplicates(body.duplicates);
      }

      toast.success(
        saveAsIncomplete
          ? `Draft saved — appears in the Incomplete queue within ~1 minute (file_id: ${body.file_id})`
          : `Referral submitted (file_id: ${body.file_id})${body.step3_triggered ? " — Step 3 pinged" : ""}`
      );

      if (!saveAsIncomplete) {
        // Reset for the next entry.
        setFields(EMPTY_FIELDS);
        setUploads([]);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Save failed";
      setErrorSummary(String(detail));
      toast.error(String(detail));
    } finally {
      setSubmitting(false);
    }
  }

  // ─── render ────────────────────────────────────────────────────
  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="manual-referral-page">
      <h1 className="text-2xl font-semibold text-slate-800 mb-1">
        Manual Referral Entry
      </h1>
      <p className="text-sm text-slate-500 mb-6">
        Types a referral end-to-end and pushes it to the pipeline exactly
        like an email-lane intake. Assigned Clinic and payor lists are
        refreshed hourly; free-typing a new payor is allowed.
      </p>

      <Section title="Case classification">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Case Type" required>
            <select
              value={fields.case_type}
              onChange={(e) => {
                const ct = e.target.value;
                setFields((f) => ({
                  ...f,
                  case_type: ct,
                  // Direct Access is PI-only.
                  direct_access: ct === "PI" ? f.direct_access : false,
                  // Clear the tab-specific requireds we won't use.
                  claim_number: ct === "PI" ? "" : f.claim_number,
                  insurance_carrier: ct === "PI" ? "" : f.insurance_carrier,
                  employer_name: ct === "PI" ? "" : f.employer_name,
                  attorney_name: ct === "WC" ? "" : f.attorney_name,
                  location_code: "", assigned_clinic: "",
                }));
              }}
              className="w-full border border-slate-300 rounded px-2 py-1"
              data-testid="case-type-select"
            >
              <option value="">Select…</option>
              <option value="PI">Personal Injury</option>
              <option value="WC">Workers&apos; Comp</option>
            </select>
          </Field>
          <Field label="Therapy Type" required>
            <select
              value={fields.therapy_type}
              onChange={(e) => setFields((f) => ({
                ...f, therapy_type: e.target.value,
                location_code: "", assigned_clinic: "",
              }))}
              className="w-full border border-slate-300 rounded px-2 py-1"
              data-testid="therapy-type-select"
            >
              <option value="">Select…</option>
              {THERAPIES.map((t) => (
                <option key={t.code} value={t.display}>{t.display}</option>
              ))}
            </select>
          </Field>
          <Field label="Assigned Clinic" required>
            {lockedToOneClinic ? (
              // Locked, and it says WHY — plain words, no jargon.
              <div>
                <div
                  className="flex items-center gap-2 px-2 py-1 text-sm font-medium text-slate-800"
                  data-testid="assigned-clinic-locked"
                >
                  <Lock size={13} className="text-slate-400" />
                  <span>{myClinics[0].label || myClinics[0].name}</span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Your location — referrals you file are assigned here automatically.
                </p>
              </div>
            ) : isLocationUser && myClinics.length === 0 ? (
              <div>
                <div
                  className="w-full border border-slate-300 rounded px-2 py-1 bg-slate-100 text-slate-500"
                  data-testid="assigned-clinic-unavailable"
                >
                  {!canLoadClinics ? "Pick Case Type + Therapy first"
                    : clinicsLoading ? "Loading…"
                    : "Not available for this case type and therapy"}
                </div>
                {canLoadClinics && !clinicsLoading && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Your location doesn&apos;t take this case type and therapy. Pick another therapy or ask WestStar staff to file it.
                  </p>
                )}
              </div>
            ) : (
            <select
              value={fields.location_code}
              onChange={(e) => {
                const c = myClinics.find((x) => x.location_code === e.target.value);
                setFields((f) => ({
                  ...f,
                  location_code: e.target.value,
                  assigned_clinic: c?.name || "",
                  assigned_clinic_address: c?.address || "",
                  assigned_clinic_city: c?.city || "",
                  assigned_clinic_zipcode: c?.zip || "",
                }));
              }}
              disabled={!canLoadClinics || clinicsLoading}
              className="w-full border border-slate-300 rounded px-2 py-1 disabled:bg-slate-100"
              data-testid="assigned-clinic-select"
            >
              <option value="">
                {!canLoadClinics ? "Pick Case Type + Therapy first" :
                 clinicsLoading ? "Loading…" :
                 myClinics.length === 0 ? "No matching clinics" : "Select…"}
              </option>
              {myClinics.map((c) => (
                <option key={c.location_code} value={c.location_code}>
                  {c.label}
                </option>
              ))}
            </select>
            )}
          </Field>
        </div>
        {isPI && (
          <div className="flex gap-6 mt-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={fields.direct_access}
                onChange={(e) => setField("direct_access", e.target.checked)}
                data-testid="direct-access-checkbox"
              />
              Direct Access
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={fields.post_op}
                onChange={(e) => setField("post_op", e.target.checked)}
                data-testid="post-op-checkbox"
              />
              Post-Op
            </label>
          </div>
        )}
        {!isPI && (
          <label className="flex items-center gap-2 text-sm mt-2">
            <input
              type="checkbox"
              checked={fields.post_op}
              onChange={(e) => setField("post_op", e.target.checked)}
              data-testid="post-op-checkbox"
            />
            Post-Op
          </label>
        )}
      </Section>

      <Section title="Patient">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="First name" required>
            <input type="text" value={fields.employee_first_name}
              onChange={(e) => setField("employee_first_name", e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1"
              data-testid="patient-first-name-input" />
          </Field>
          <Field label="Last name" required>
            <input type="text" value={fields.employee_last_name}
              onChange={(e) => setField("employee_last_name", e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1"
              data-testid="patient-last-name-input" />
          </Field>
          <Field label="Date of Birth" required hint="Stored as YYYY-MM-DD">
            <input type="date" value={fields.date_of_birth}
              onChange={(e) => setField("date_of_birth", e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1"
              data-testid="patient-dob-input" />
          </Field>
          <Field label="Birth Sex">
            <select value={fields.patient_birth_sex}
              onChange={(e) => setField("patient_birth_sex", e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1"
              data-testid="patient-birth-sex-select">
              <option value="">Blank / unknown</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
            </select>
          </Field>
          <Field label="Phone" required hint="10 digits">
            <input type="tel" value={fields.patient_home_phone}
              onChange={(e) => setField("patient_home_phone", e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1"
              data-testid="patient-phone-input" />
          </Field>
          {/* SMS consent (Consent v0.3, 2026-09-05). Placed with the
              patient contact fields, immediately under Phone, because it is
              consent for THAT number. The full language sits behind a
              details expander so it is always reachable from the checkbox —
              10DLC opt-in evidence. */}
          <div className="md:col-span-2" data-testid="sms-consent-block">
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                className="mt-0.5"
                data-testid="sms-consent-checkbox"
              />
              <span data-testid="sms-consent-label">{SMS_CONSENT_CHECKBOX_TEXT}</span>
            </label>
            <details className="mt-1 ml-6">
              <summary
                className="text-xs text-teal-700 cursor-pointer"
                data-testid="sms-consent-details-toggle"
              >
                Full text message consent language (v0.3)
              </summary>
              <p className="mt-1 text-xs text-slate-500 leading-relaxed"
                 data-testid="sms-consent-full-text">
                {SMS_CONSENT_FULL_TEXT}
              </p>
            </details>
          </div>
          <Field label="SSN">
            <input type="text" value={fields.patient_ssn}
              onChange={(e) => setField("patient_ssn", e.target.value)}
              placeholder="XXX-XX-XXXX (optional)"
              className="w-full border border-slate-300 rounded px-2 py-1"
              data-testid="patient-ssn-input" />
          </Field>
          <Field label="Street address" required>
            <input type="text" value={fields.patient_address}
              onChange={(e) => setField("patient_address", e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1"
              data-testid="patient-address-input" />
          </Field>
          <Field label="City" required>
            <input type="text" value={fields.patient_city}
              onChange={(e) => setField("patient_city", e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1"
              data-testid="patient-city-input" />
          </Field>
          <Field label="State" required>
            <select value={fields.patient_state}
              onChange={(e) => setField("patient_state", e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1"
              data-testid="patient-state-select">
              <option value="">Select…</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="ZIP" required hint="5 digits">
            <input type="text" value={fields.patient_zip_code}
              onChange={(e) => setField("patient_zip_code", e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1"
              data-testid="patient-zip-input" />
          </Field>
        </div>
      </Section>

      <Section title="Referral details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Body parts involved" required>
            <input type="text" value={fields.body_parts_involved}
              onChange={(e) => setField("body_parts_involved", e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1"
              data-testid="body-parts-input" />
          </Field>
          <Field label="Referral reason" required>
            <input type="text" value={fields.referral_reason}
              onChange={(e) => setField("referral_reason", e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1"
              data-testid="referral-reason-input" />
          </Field>
          <Field label="Type of accident">
            <input type="text" value={fields.type_of_accident}
              onChange={(e) => setField("type_of_accident", e.target.value)}
              placeholder="MVA, slip & fall, etc."
              className="w-full border border-slate-300 rounded px-2 py-1" />
          </Field>
          {isPI && (
            <Field label="Date of Accident" required hint="YYYY-MM-DD; not future">
              <input type="date" value={fields.date_of_accident}
                onChange={(e) => setField("date_of_accident", e.target.value)}
                max={localTodayYYYYMMDD()}
                className="w-full border border-slate-300 rounded px-2 py-1"
                data-testid="date-of-accident-input" />
            </Field>
          )}
          {isWC && (
            <Field label="Date of Injury" required hint="YYYY-MM-DD; not future">
              <input type="date" value={fields.date_of_injury}
                onChange={(e) => setField("date_of_injury", e.target.value)}
                max={localTodayYYYYMMDD()}
                className="w-full border border-slate-300 rounded px-2 py-1"
                data-testid="date-of-injury-input" />
            </Field>
          )}
          <Field label="ICD-10 codes"
            hint="Comma-separated, primary first. Optional.">
            <input type="text" value={fields.icd10_codes}
              onChange={(e) => setField("icd10_codes", e.target.value)}
              placeholder="M54.50, M25.561"
              className="w-full border border-slate-300 rounded px-2 py-1"
              data-testid="icd10-input" />
          </Field>
        </div>
      </Section>

      {!fields.direct_access && (
        <Section title="Referring physician">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name" required={!fields.direct_access}>
              <PhysicianAutocomplete
                value={fields.primary_physician_name}
                onChange={(v) => setField("primary_physician_name", v)}
                onPick={(p) => setFields((f) => ({
                  ...f,
                  // Ticket §4 — render "First Last" in the form field
                  // (roster stores "Last, First"). NPI is the prize;
                  // free-typed unknown physicians still work.
                  primary_physician_name: p.display_name || p.name || f.primary_physician_name,
                  primary_physician_npi: p.npi || f.primary_physician_npi,
                  primary_physician_phone: p.phone || f.primary_physician_phone,
                  primary_physician_address: p.address || f.primary_physician_address,
                }))}
                required={!fields.direct_access}
              />
            </Field>
            <Field label="NPI" hint="10 digits (recommended)">
              <input type="text" value={fields.primary_physician_npi}
                onChange={(e) => setField("primary_physician_npi", e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1"
                data-testid="physician-npi-input" />
            </Field>
            <Field label="Phone">
              <input type="tel" value={fields.primary_physician_phone}
                onChange={(e) => setField("primary_physician_phone", e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1" />
            </Field>
            <Field label="Address">
              <input type="text" value={fields.primary_physician_address}
                onChange={(e) => setField("primary_physician_address", e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1" />
            </Field>
          </div>
        </Section>
      )}

      {isPI && (
        <Section title="Attorney / Law firm (PI payor)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Firm / Attorney name" required>
              <PayorAutocomplete
                caseType="PI"
                value={fields.attorney_name}
                selectedCode={fields.attorney_payor_code}
                onChange={(v) => setFields((f) => ({
                  ...f,
                  attorney_name: v,
                  // §3: free-typing clears the hidden code — the name
                  // is now diverging from the roster row.
                  attorney_payor_code: "",
                }))}
                onPick={(p) => setFields((f) => ({
                  ...f, attorney_name: p.name,
                  attorney_address: p.address || f.attorney_address,
                  attorney_city: p.city || f.attorney_city,
                  attorney_state: p.state || f.attorney_state,
                  attorney_zip_code: p.zip_code || f.attorney_zip_code,
                  attorney_phone: p.phone || f.attorney_phone,
                  attorney_contact_email: p.email || f.attorney_contact_email,
                  // §3: hidden code from the roster row.
                  attorney_payor_code: p.code || "",
                }))}
                required
              />
            </Field>
            <Field label="Phone" required>
              <input type="tel" value={fields.attorney_phone}
                onChange={(e) => setField("attorney_phone", e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1"
                data-testid="attorney-phone-input" />
            </Field>
            <Field label="Contact person">
              <input type="text" value={fields.attorney_contact_person}
                onChange={(e) => setField("attorney_contact_person", e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1" />
            </Field>
            <Field label="Contact email">
              <input type="email" value={fields.attorney_contact_email}
                onChange={(e) => setField("attorney_contact_email", e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1" />
            </Field>
          </div>
        </Section>
      )}

      {isWC && (
        <Section title="Employer & Insurance">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Employer" required>
              <input type="text" value={fields.employer_name}
                onChange={(e) => setField("employer_name", e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1"
                data-testid="employer-input" />
            </Field>
            <Field label="Claim number" required>
              <input type="text" value={fields.claim_number}
                onChange={(e) => setField("claim_number", e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1"
                data-testid="claim-number-input" />
            </Field>
            <Field label="Insurance carrier" required>
              <PayorAutocomplete
                caseType="WC"
                value={fields.insurance_carrier}
                selectedCode={fields.insurance_payor_code}
                onChange={(v) => setFields((f) => ({
                  ...f,
                  insurance_carrier: v,
                  // §3: free-typing clears the hidden code.
                  insurance_payor_code: "",
                }))}
                onPick={(p) => setFields((f) => ({
                  ...f,
                  insurance_carrier: p.name,
                  insurance_address: p.address || f.insurance_address,
                  // §3: hidden code from the roster row.
                  insurance_payor_code: p.code || "",
                }))}
                required
              />
            </Field>
            <Field label="Adjuster name">
              <input type="text" value={fields.adjuster_name}
                onChange={(e) => setField("adjuster_name", e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1" />
            </Field>
            <Field label="Adjuster phone">
              <input type="tel" value={fields.adjuster_phone}
                onChange={(e) => setField("adjuster_phone", e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1" />
            </Field>
            <Field label="Adjuster email">
              <input type="email" value={fields.adjuster_email}
                onChange={(e) => setField("adjuster_email", e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1" />
            </Field>
          </div>
        </Section>
      )}

      <Section title="Documents">
        <div className="text-sm text-slate-600 mb-2">
          PDF, DOC, JPG, PNG, GIF, BMP, TIFF, WEBP, HEIC. Email Chain must be
          PDF and only one may be attached.
        </div>
        <input
          type="file" multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.tif,.tiff,.webp,.heic"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          className="mb-2"
          data-testid="documents-input"
        />
        {uploads.length > 0 && (
          <ul className="border border-slate-200 rounded divide-y" data-testid="uploads-list">
            {uploads.map((u, idx) => (
              <li key={idx} className="flex items-center gap-2 p-2 text-sm"
                  data-testid={`upload-row-${idx}`}>
                <span className="flex-1 truncate">{u.file.name}</span>
                <select value={u.label}
                  onChange={(e) => setLabel(idx, e.target.value)}
                  className="border border-slate-300 rounded px-1 py-0.5"
                  data-testid={`upload-label-${idx}`}>
                  {DOC_LABELS.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
                <button type="button"
                  onClick={() => removeUpload(idx)}
                  className="text-red-600 text-xs">Remove</button>
              </li>
            ))}
          </ul>
        )}
        {emailChainCount > 1 && (
          <div className="text-xs text-red-600 mt-1">
            Only one Email Chain document is allowed.
          </div>
        )}
      </Section>

      <Section title="Notes">
        <textarea value={fields.team_notes}
          onChange={(e) => setField("team_notes", e.target.value)}
          rows={3}
          className="w-full border border-slate-300 rounded px-2 py-1"
          placeholder="Team notes (optional)"
          data-testid="team-notes-input" />
      </Section>

      {errorSummary && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-2 mb-4 rounded"
             data-testid="submit-error">
          {errorSummary}
        </div>
      )}

      <div className="flex justify-end gap-2 sticky bottom-0 bg-white/90 backdrop-blur py-3 border-t">
        <button
          type="button"
          disabled={submitting}
          onClick={() => doSubmit(true)}
          className="px-3 py-1.5 border border-slate-300 rounded text-sm disabled:opacity-60"
          data-testid="save-as-incomplete-btn"
        >
          Save as Incomplete
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={() => doSubmit(false)}
          className="px-4 py-1.5 bg-slate-800 text-white rounded text-sm disabled:opacity-60"
          data-testid="submit-btn"
        >
          {submitting ? "Saving…" : "Save & Sync to Raintree"}
        </button>
      </div>

      {/* Non-blocking duplicate warning (§9.1) */}
      {duplicates && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40"
             data-testid="duplicate-warning-modal">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-slate-800 mb-2">
              Possible duplicate
            </h2>
            <p className="text-sm text-slate-600 mb-3">
              Referral saved. A same-last-name + same-DOB match was found in
              the last 30 days:
            </p>
            <ul className="text-sm max-h-40 overflow-auto border rounded p-2 mb-4">
              {duplicates.map((d) => (
                <li key={d.file_id} className="py-1">
                  <span className="font-medium">{d.patient_name}</span>
                  {" "}<span className="text-slate-500">
                    ({d.intake_status}, DOB {d.date_of_birth})
                  </span>
                </li>
              ))}
            </ul>
            <div className="text-right">
              <button
                onClick={() => setDuplicates(null)}
                className="px-3 py-1 bg-slate-800 text-white rounded text-sm"
                data-testid="duplicate-warning-dismiss">
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
